import { Link } from 'react-router-dom';
import {
  Brain,
  BookOpen,
  Trophy,
  Layers,
  BarChart3,
  Clock,
  Target,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Play,
  RefreshCw,
  Award,
  Lightbulb,
  MousePointer,
  Smartphone
} from 'lucide-react';

const HowItWorks = () => {
  return (
    <div className="min-h-screen bg-dark-950 pt-24 pb-16">
      {/* Header */}
      <section className="relative pb-16">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/20 rounded-full blur-3xl"></div>
          <div className="absolute top-20 -left-40 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Jak používat <span className="gradient-text">Synapso</span>
          </h1>
          <p className="text-lg text-dark-400 max-w-2xl mx-auto">
            Kompletní průvodce aplikací pro přípravu na přijímací zkoušky na lékařskou fakultu.
          </p>
        </div>
      </section>

      {/* Quick Overview */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { icon: Brain, title: 'Kvízy', desc: 'Vyber si předmět', color: 'from-green-500 to-emerald-600' },
            { icon: Trophy, title: 'Simulace', desc: 'Testy jako u přijímaček', color: 'from-amber-500 to-orange-600' },
            { icon: Layers, title: 'Flashcards', desc: 'Kartičky pro efektivní učení', color: 'from-purple-500 to-violet-600' },
            { icon: BarChart3, title: 'Dashboard', desc: 'Sleduj svůj pokrok a statistiky', color: 'from-blue-500 to-indigo-600' },
          ].map((item, i) => (
            <div key={i} className="glass rounded-2xl p-6 text-center">
              <div className={`w-14 h-14 mx-auto rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-4`}>
                <item.icon size={28} className="text-white" />
              </div>
              <h3 className="text-white font-semibold mb-2">{item.title}</h3>
              <p className="text-dark-400 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Detailed Sections */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">

        {/* 1. Kvízy */}
        <section id="quizzes" className="scroll-mt-24">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Brain size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Kvízy podle předmětů</h2>
              <p className="text-dark-400">Procvičuj biologii, chemii, fyziku a somatologii</p>
            </div>
          </div>

          <div className="glass rounded-2xl p-6 space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <MousePointer size={16} className="text-green-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Vyber si předmět</h4>
                <p className="text-dark-400 text-sm">Na hlavní stránce klikni na jeden ze čtyř předmětů: Biologie, Chemie, Fyzika nebo Somatologie.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <Play size={16} className="text-green-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Odpovídej na otázky</h4>
                <p className="text-dark-400 text-sm">Každá otázka má 4 možnosti. U každé otázky ihned vidíš, zda je správně.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <Lightbulb size={16} className="text-green-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Prostuduj vysvětlení</h4>
                <p className="text-dark-400 text-sm">Po každé odpovědi se zobrazí podrobné vysvětlení. Díky tomu pochopíš látku do hloubky, nejen správnou odpověď.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <RefreshCw size={16} className="text-green-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Pokračuj nebo začni znovu</h4>
                <p className="text-dark-400 text-sm">Po dokončení sady můžeš pokračovat s dalšími otázkami nebo začít od začátku pro opakování.</p>
              </div>
            </div>

            <div className="mt-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <p className="text-amber-400 text-sm flex items-center gap-2">
                <Sparkles size={16} />
                <span><strong>Free verze:</strong> 10 otázek z každého předmětu zdarma. S předplatným máš přístup ke všem 600+ otázkám.</span>
              </p>
            </div>
          </div>
        </section>

        {/* 2. Simulace */}
        <section id="simulation" className="scroll-mt-24">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Trophy size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Simulace přijímaček</h2>
              <p className="text-dark-400">Testy jako při skutečných přijímačkách</p>
            </div>
          </div>

          <div className="glass rounded-2xl p-6 space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <Clock size={16} className="text-amber-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Časový limit</h4>
                <p className="text-dark-400 text-sm">Simulace má nastaven časový limit stejně jako skutečné přijímačky. Nauč se pracovat pod tlakem a správně si rozvrhnout čas.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <Target size={16} className="text-amber-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Mix všech předmětů</h4>
                <p className="text-dark-400 text-sm">Simulace obsahuje otázky ze všech předmětů najednou - biologie, chemie a fyzika. Přesně jako u přijímaček.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <Award size={16} className="text-amber-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Analyzuj svoje výsledky</h4>
                <p className="text-dark-400 text-sm">Detailní rozbor každého testu - uvidíš své skóre, správné odpovědi a oblasti ke zlepšení.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <RefreshCw size={16} className="text-amber-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Procvičování chybných odpovědí</h4>
                <p className="text-dark-400 text-sm">Po testu můžeš znovu procvičit otázky, které jsi zodpověděl špatně. Zaměř se přesně na to, co potřebuješ.</p>
              </div>
            </div>

            <div className="mt-4 p-4 rounded-xl bg-primary-500/10 border border-primary-500/20">
              <p className="text-primary-400 text-sm flex items-center gap-2">
                <CheckCircle size={16} />
                <span><strong>Tip:</strong> Zkus si simulaci alespoň jednou týdně, abys sledoval svůj pokrok a zvykl si na formát testu.</span>
              </p>
            </div>
          </div>
        </section>

        {/* 3. Flashcards */}
        <section id="flashcards" className="scroll-mt-24">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
              <Layers size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Flashcards (Kartičky)</h2>
              <p className="text-dark-400">Efektivní metoda pro zapamatování pojmů a definic</p>
            </div>
          </div>

          <div className="glass rounded-2xl p-6 space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <BookOpen size={16} className="text-purple-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Vyber kategorii</h4>
                <p className="text-dark-400 text-sm">Zvol si předmět a téma, které chceš procvičovat. Kartičky jsou rozděleny do logických celků.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <RefreshCw size={16} className="text-purple-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Otáčej kartičky</h4>
                <p className="text-dark-400 text-sm">Kliknutím na kartičku ji otočíš a uvidíš odpověď. Zkus si nejprve vzpomenout sám, pak zkontroluj.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <CheckCircle size={16} className="text-purple-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Označ jako naučené</h4>
                <p className="text-dark-400 text-sm">Kartičky, které už umíš, můžeš označit. Systém ti pak bude přednostně ukazovat ty, které ještě potřebuješ procvičit.</p>
              </div>
            </div>

            <div className="mt-4 p-4 rounded-xl bg-purple-500/10 border border-purple-500/20">
              <p className="text-purple-400 text-sm flex items-center gap-2">
                <Sparkles size={16} />
                <span><strong>Metoda:</strong> Flashcards využívají princip aktivního vybavování, což je jedna z nejúčinnějších metod učení.</span>
              </p>
            </div>
          </div>
        </section>

        {/* 4. Dashboard */}
        <section id="dashboard" className="scroll-mt-24">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <BarChart3 size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Dashboard a statistiky</h2>
              <p className="text-dark-400">Sleduj svůj pokrok a výsledky</p>
            </div>
          </div>

          <div className="glass rounded-2xl p-6 space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <BarChart3 size={16} className="text-blue-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Přehled výsledků</h4>
                <p className="text-dark-400 text-sm">Na dashboardu vidíš souhrnné statistiky - kolik otázek jsi zodpověděl, úspěšnost podle předmětů a trend v čase.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <Trophy size={16} className="text-blue-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Historie simulací</h4>
                <p className="text-dark-400 text-sm">Všechny tvé simulace se ukládají. Můžeš sledovat, jak se zlepšuješ, a porovnávat výsledky v čase.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <Target size={16} className="text-blue-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Identifikuj slabiny</h4>
                <p className="text-dark-400 text-sm">Statistiky ti ukáží, které předměty nebo témata potřebuješ více procvičovat. Zaměř se na oblasti s nižší úspěšností.</p>
              </div>
            </div>
          </div>
        </section>

        {/* 5. Mobilní aplikace */}
        <section id="mobile" className="scroll-mt-24">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
              <Smartphone size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Mobilní aplikace</h2>
              <p className="text-dark-400">Uč se kdykoliv a kdekoliv</p>
            </div>
          </div>

          <div className="glass rounded-2xl p-6">
            <p className="text-dark-400 mb-4">
              Synapso je dostupné jako nativní aplikace pro iOS i Android. Stáhni si aplikaci a uč se v tramvaji, ve frontě nebo kdykoliv máš volnou chvilku.
            </p>
            <ul className="space-y-2 text-dark-400">
              <li className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-400" />
                <span>Offline režim pro učení bez internetu</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-400" />
                <span>Push notifikace pro připomenutí učení</span>
              </li>
            </ul>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center pt-8">
          <div className="glass rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Připraven začít?</h2>
            <p className="text-dark-400 mb-6">
              Zaregistruj se zdarma a začni se připravovat na přijímačky ještě dnes.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-xl gradient-bg text-white font-semibold hover:opacity-90 transition-opacity"
            >
              Vytvořit účet zdarma
              <ArrowRight size={20} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HowItWorks;
